import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import {
    FormationDataServiceName,
    FormationEndpoints,
    IFormationDataService,
} from '../../../DataServices/FormationDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { BaseApiDataResponse } from '../../../Models/ApiData';
import {
    FormationDetails,
    FormationDetailsApiDataResponse,
    FormationListOrder,
    FormationListResponse,
} from '../../../Models/Formation/Formation';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { AuthServiceName, IAuthService } from '../Authentication/AuthenticationService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface IFormationService {
    listFormation: (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: FormationListOrder;
        }
    ) => Promise<FormationListResponse>;

    getFormation: (
        cts: XHRRequestCanceler,
        formationId: string
    ) => Promise<FormationDetailsApiDataResponse>;

    updateFormation: (
        formationId: string,
        formation: FormationDetails,
        cts: XHRRequestCanceler
    ) => Promise<FormationDetailsApiDataResponse>;

    createFormation: (
        formation: FormationDetails,
        cts: XHRRequestCanceler
    ) => Promise<FormationDetailsApiDataResponse>;

    exportFormationList: (
        cts: XHRRequestCanceler,
        search: string,
        order?: FormationListOrder
    ) => Promise<void>;

    deleteFormation: (formationId: string, cts: XHRRequestCanceler) => Promise<BaseApiDataResponse>;
}

class FormationService extends BaseReduxService implements IFormationService {
    private readonly formationDataService: Lazy<IFormationDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly downloadService: Lazy<IDownloadService>;
    private readonly authService: Lazy<IAuthService>;

    constructor() {
        super();
        this.formationDataService = IoC.getLazy<IFormationDataService>(FormationDataServiceName);
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
        this.authService = IoC.getLazy<IAuthService>(AuthServiceName);
    }

    deleteFormation = (
        formationId: string,
        cts: XHRRequestCanceler
    ): Promise<BaseApiDataResponse> => {
        try {
            return this.formationDataService.value().deleteFormation(formationId, cts);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    listFormation = (
        cts: XHRRequestCanceler,
        params: { search?: string | undefined; order?: FormationListOrder | undefined }
    ): Promise<FormationListResponse> => {
        try {
            return this.formationDataService.value().listFormation(
                {
                    search: params.search,
                    order: params.order,
                },
                cts
            );
        } catch (err) {
            return Promise.reject(err);
        }
    };

    getFormation = (
        cts: XHRRequestCanceler,
        formationId: string
    ): Promise<FormationDetailsApiDataResponse> => {
        try {
            return this.formationDataService.value().getFormation(cts, formationId);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    updateFormation = async (
        formationId: string,
        formation: FormationDetails,
        cts: XHRRequestCanceler
    ): Promise<FormationDetailsApiDataResponse> => {
        try {
            const response = await this.formationDataService
                .value()
                .updateFormation(formationId, formation, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    createFormation = async (
        formation: FormationDetails,
        cts: XHRRequestCanceler
    ): Promise<FormationDetailsApiDataResponse> => {
        try {
            const response = await this.formationDataService
                .value()
                .createFormation(formation, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            this.toastApiError(this.i18nService.value().getString(`Error_${err.error_code}` || ''));
            return Promise.reject(err);
        }
    };

    exportFormationList = async (
        cts: XHRRequestCanceler,
        search: string,
        order?: FormationListOrder | undefined
    ): Promise<void> => {
        try {
            const params: { [key: string]: string } = {};
            params['search'] = search;
            if (order) {
                params['order'] = order.valueOf();
            }
            const url: string = ExportUtils.getExportUrlWithParams(
                {
                    url: FormationEndpoints.EXPORT_FORMATION_LIST,
                    auth: await this.authService.value().getAuthorization(),
                },
                params
            );
            this.downloadService.value().downloadFile(url, 'Formations', ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const FormationServiceName = 'FormationService';
export { FormationService, FormationServiceName };
